import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import type { ReportUploadedFile } from "@/api/report/_models/ReportUpload";
import { useSnapshotSection } from "@/components/snapshot/snapshotSection.context";

interface SnapshotDropzoneFileActionsProps {
  /** The file in S3; undefined until the refetched list has it (not deletable yet). */
  file: ReportUploadedFile | undefined;
}

interface SnapshotDropzoneTagProps {
  label: string;
  isNew?: boolean;
}

const SnapshotDropzoneTag = (props: SnapshotDropzoneTagProps) => {
  const { label, isNew = false } = props;
  return (
    <Box
      component="span"
      sx={{
        bgcolor: isNew ? "surface.warning" : "action.selected",
        borderRadius: 0.5,
        color: isNew ? "warning.main" : "text.secondary",
        fontSize: 12,
        fontWeight: 600,
        px: 1,
        py: 0.125,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
};

/**
 * An uploaded file's tags (duplicate name, new since the build) and its delete button; while the
 * section asks about this file, the inline "Delete this file?" confirm instead.
 */
export const SnapshotDropzoneFileActions = (props: SnapshotDropzoneFileActionsProps) => {
  const { file } = props;
  const section = useSnapshotSection();
  const deletion = section.deletion;

  if (file && deletion.file?.key === file.key) {
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center" }}
      >
        <Button
          disabled={deletion.isDeleting}
          onClick={deletion.cancel}
          size="small"
        >
          Cancel
        </Button>
        <Button
          color="error"
          disabled={deletion.isDeleting}
          onClick={deletion.confirm}
          size="small"
          variant="contained"
        >
          Delete
        </Button>
      </Stack>
    );
  }

  let duplicateTag: string | null = null;
  const duplicate = file ? section.duplicates.tagByKey.get(file.key) : undefined;
  if (duplicate === "newest") duplicateTag = "Duplicate · newest";
  else if (duplicate === "older") duplicateTag = "Duplicate · older";
  const isNew = file !== undefined && section.newSinceBuildKeys.includes(file.key);

  const handleDelete = () => {
    if (file) deletion.ask(file);
  };

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ alignItems: "center" }}
    >
      {duplicateTag && <SnapshotDropzoneTag label={duplicateTag} />}
      {isNew && (
        <SnapshotDropzoneTag
          isNew
          label="New since build"
        />
      )}
      <IconButton
        aria-label={`Delete ${file?.filename ?? "file"}`}
        disabled={section.isBuilding || !file}
        onClick={handleDelete}
        size="small"
        sx={{ "&:hover": { color: "error.main" } }}
      >
        <DeleteOutlinedIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};
